<template>
  <MobileModal />
  <MobileHelpSlider />

  <!-- Navigation Bar for Multiple Products Mode -->
  <div
      v-if="leadGen.multiple"
      class="navigation-bar tw-mb-6"
  >
    <div
        class="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-p-4 tw-shadow-sm tw-gap-3">
      <button
          class="tw-flex tw-items-center tw-px-3 sm:tw-px-4 tw-py-2 tw-text-gray-700 hover:tw-text-gray-900 tw-transition-colors tw-text-sm sm:tw-text-base"
          @click="$emit('back-to-products')"
      >
        <svg
            class="tw-w-5 tw-h-5 tw-mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
          <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        <span class="tw-hidden sm:tw-inline">Back to Products</span>
        <span class="tw-inline sm:tw-hidden">Back</span>
      </button>

      <div class="tw-text-center tw-hidden sm:tw-block">
        <h2 class="tw-text-lg tw-font-medium tw-text-gray-800">
          Customizing Product
        </h2>
      </div>

      <button
          v-if="cartItemsCount > 0"
          class="tw-flex tw-items-center tw-px-3 sm:tw-px-4 tw-py-2 tw-bg-green-600 tw-text-white tw-rounded-lg hover:tw-bg-green-700 tw-transition-colors tw-text-sm sm:tw-text-base"
          @click="$emit('view-quote')"
      >
        <svg
            class="tw-w-5 tw-h-5 tw-mr-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
        >
          <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17M17 13v6m0 0v2m0-2h2m-2 0h-2"
          />
        </svg>
        View Quote
        <span class="tw-ml-2 tw-bg-white tw-text-green-600 tw-text-xs tw-font-bold tw-px-2 tw-py-1 tw-rounded-full">
          {{ cartItemsCount }}
        </span>
      </button>
      <div
          v-else
          class="tw-hidden sm:tw-block tw-w-32"
      /> <!-- Spacer for alignment when no cart items -->
    </div>
  </div>

  <div class="tw-grid sm:tw-grid-cols-12 tw-grid-cols-1 lg:tw-flex-row gap-6">
    <loading
        v-model:active="designerStore.variantLoading"
        :can-cancel="false"
        :is-full-page="true"
    />

    <div class="tw-col-span-5">
      <h1
          class="mb-4"
          style="font-weight: bold; font-size: 1.8em"
      >
        {{ designerStore.product.object.name }}
      </h1>

      <div
          style="padding-bottom: 20px"
          v-html="designerStore.product.object.description"
      />


      <template
          v-if="designerStore.product.object.dimensional_uplifts && designerStore.product.object.dimensions_location == 1"
      >
        <DimensionsInput
            @layer-update="layerUpdate"
        />
      </template>

      <VariantSelector
          @variant-changed="variantUpdate"
      />

      <template
          v-if="designerStore.product.object.dimensional_uplifts && designerStore.product.object.dimensions_location == 2"
      >
        <DimensionsInput
            @layer-update="layerUpdate"
        />
      </template>

      <template
          v-for="layer in getLayersOrdered"
          :key="layer.id"
      >
        <LayerSelector
            v-if="!layer.empty"
            :layer="layer"
            @layer-update="layerUpdate"
        />
      </template>

      <template
          v-if="designerStore.product.object.dimensional_uplifts && designerStore.product.object.dimensions_location == 0"
      >
        <DimensionsInput
            @layer-update="layerUpdate"
        />
      </template>

      <div class="tw-mt-8">
        <button
            class="button tw-w-full"
            :style="{ backgroundColor: settings.primary_colour }"
            @click="handleMainButtonClick"
        >
          {{ leadGen.multiple ? 'Add to Cart' : 'Request a Quote' }}
        </button>
      </div>

    </div>

    <div
        class="tw-col-span-5 md:tw-sticky tw-relative"
        style="align-self: flex-end;bottom: 1rem;"
    >
      <div
          class="tw-relative"
          style="padding-top: 50px;"
      >
        <ImageLayering/>

        <div
            v-if="designerStore.pricing.price"
            class="tw-text-center"
        >
          <h6>Your Price</h6>
          <span class="tw-original-price tw-line-through"/>
          <span class="tw-text-lg">£<span id="pricing">{{ designerStore.pricing.price }}</span></span>
          <span class="tw-text-gray-500 tw-font-bold tw-px-1.5">Inc Vat</span>
          <br>
          <br>
          <span class="tw-hidden md:tw-block">
            <button
                class="button tw-w-[300px]"
                :style="{ backgroundColor: settings.primary_colour }"
                @click="handleMainButtonClick"
            >
              {{ leadGen.multiple ? 'Add to Cart' : 'Request a Quote' }}
            </button>
          </span>
        </div>
      </div>
    </div>

    <MobileToolbar/>
    <div
        class="tw-col-span-2 md:tw-sticky tw-relative"
        style="align-self: flex-end;bottom: 1rem;"
    >
      <transition name="fade">
        <HelpContainer
            v-if="designerStore.helpArticle && designerStore.itemType !== 'quote'"
        />
      </transition>
    </div>
  </div>
</template>

<script>
import {useCoreStore} from "@/stores/CoreStore";
import {useDesignerStore} from '@/stores/DesignerStore';
import VariantSelector from '@/components/backend/pages/designer/components/List/VariantSelector.vue'
import LayerSelector from '@/components/backend/pages/designer/components//List/LayerSelector.vue'
import ImageLayering from '@/components/backend/pages/designer/components//List/ImageLayering.vue'
import HelpContainer from '@/components/backend/pages/designer/components//List/HelpContainer.vue'
import DimensionsInput from '@/components/backend/pages/designer/components//List/DimensionsInput.vue'
import Loading from 'vue-loading-overlay';
import MobileToolbar from "@/components/backend/pages/designer/components/List/MobileToolbar.vue";
import MobileModal from "@/components/backend/pages/designer/components/List/MobileModal.vue";
import MobileHelpSlider from "@/components/backend/pages/designer/components/List/MobileHelpSlider.vue";
import 'vue-loading-overlay/dist/css/index.css';
// import ProductSections from '@/components/backend/pages/designer/components/List/ProductSections.vue'
export default {
  name: "LeadGeneratorLayout",
  components: {
    VariantSelector,
    LayerSelector,
    ImageLayering,
    HelpContainer,
    DimensionsInput,
    Loading,
    MobileToolbar,
    MobileModal,
    MobileHelpSlider,
    // ProductSections,
  },
  inject: ['coreService', 'designerService'],
  props: {
    leadGen: {
      type: Object,
      required: true,
    },
    settings: {
      type: Object,
      required: true,
    },
    selectedProduct: {
      type: Object,
      default: null,
    },
    cartItemsCount: {
      type: Number,
      default: 0,
    },
  },
  setup() {
    const designerStore = useDesignerStore();
    const coreStore = useCoreStore();
    return {designerStore, coreStore};
  },
  data() {
    return {}
  },
  computed: {
    getLayersOrdered() {
      return Object.values(this.designerStore.product.layers).sort((a, b) => b.weight - a.weight)
    },
    getSections() {
      const sections = this.designerStore.product.object.sections; // Replace with store path in your app

      if (!sections || !Array.isArray(sections)) {
        return []; // Return an empty array if the data is not as expected
      }

      // Use map to create a new array with the added 'id' property
      return sections.map((section, index) => {
        return {
          ...section, // Spread the existing properties of the section
          id: index    // Add the new 'id' property with the index value
        };
      });
    }
  },
  methods: {
    variantUpdate() {
      this.designerService.loadVariant()
    },
    layerUpdate(layerId = null) {
      if (this.designerService.runUpdates) {
        this.designerService.runUpdates(layerId)
      }
    },
    productChanged(productId) {
      this.designerStore.loading = true;
      this.designerService.getProduct(productId)
    },
    handleMainButtonClick() {
      console.log('Button clicked! Multiple mode:', this.leadGen.multiple);
      if (this.leadGen.multiple) {
        // Multi-product mode: add to cart
        console.log('Emitting add-to-cart');
        this.$emit('add-to-cart', {
          id: this.selectedProduct ? this.selectedProduct.id : this.designerStore.product.object.id,
          name: this.selectedProduct ? this.selectedProduct.name : this.designerStore.product.object.name,
          quantity: 1,
          price: this.designerStore.product.object.price,
          images: this.designerStore.images,
          description: this.designerStore.description,
          raw_data: this.designerStore.selectedOptions,
        });
      } else {
        // Single product mode: show quote form
        this.$emit('clear-cart');
        this.$emit('add-to-cart', {
          id: this.selectedProduct ? this.selectedProduct.id : this.designerStore.product.object.id,
          name: this.selectedProduct ? this.selectedProduct.name : this.designerStore.product.object.name,
          quantity: 1,
          price: this.designerStore.product.object.price,
          images: this.designerStore.images,
          description: this.designerStore.description,
          raw_data: this.designerStore.selectedOptions,
        });
        this.$emit('show-quote-form');
      }
    }
  }
}
</script>

<style scoped>
/* Previous styles from your component... */
.help-tip {
  position: absolute;
  text-align: center;
  background-color: #29b6f6;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  z-index: 1000;
  font-size: 12px;
  line-height: 26px;
  cursor: default;
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.help-tip:hover {
  cursor: pointer;
  background-color: #ccc;
}

.help-tip:before {
  content: '?';
  font-weight: 700;
  color: #fff;
}

.help-tip p {
  visibility: hidden;
  opacity: 0;
  z-index: 1000;
  text-align: left;
  background-color: #039be5;
  padding: 20px;
  width: 300px;
  position: absolute;
  border-radius: 4px;
  right: -4px;
  color: #fff;
  font-size: 13px;
  line-height: normal;
  transform: scale(0.7);
  transform-origin: 100% 0%;
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}

.help-tip:hover p {
  cursor: default;
  visibility: visible;
  opacity: 1;
  transform: scale(1.0);
}

.help-tip p:before {
  position: absolute;
  content: '';
  width: 0;
  height: 0;
  border: 6px solid transparent;
  border-bottom-color: #039be5;
  right: 10px;
  top: -12px;
}

.help-tip p:after {
  width: 100%;
  height: 40px;
  content: '';
  position: absolute;
  top: -5px;
  left: 0;
  z-index: 10000;
}

.help-tip a {
  color: #fff;
  font-weight: 700;
}

.help-tip a:hover, .help-tip a:focus {
  color: #fff;
  text-decoration: underline;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .5s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */
{
  opacity: 0;
}

.button {
  border: none;
  color: white;
  padding: 10px 16px;
  text-align: center;
  text-decoration: none;
  display: inline-block;
  font-size: 16px;
  border-radius: 100px;
  cursor: pointer;
}

/* Styles for Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9998;
}

.modal-content {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  z-index: 9999;
}
</style>